import React, { useCallback, useEffect, useMemo, useState, } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Descriptions, Spin, message } from "antd";
import { formatDistance } from "@/utils/utils";
import { getSingleAdminShipment } from "@/services/shipments";
import { getAvailableTransporterShipment } from "@/services/shipments_new";
import { ShipmentTagsList } from "../ShipmentRequirementsSummary";
import ETAInputNavBar from "./ETAInputNavBar";
import EnterETAsInputForm from "./EnterETAsInputForm";
import EnterExchangeETAs from "./EnterExchangeETAs";
import ExchangeOfferAlert from "./ExchangeOfferAlert";
const EnterETAsInputBody = ({ shipmentsList, setShipmentsList, etasInputFormRef, etasValidatedForAllShipments, hideConfirmButton, configPerm, }) => {
    /*
      Creates a function to update the specified property on the specified shipment in the dispatch list
    */
    const createSingleShipmentPropertyValueUpdater = useCallback((id, propertyName) => {
        const updater = (updatedValue) => {
            setShipmentsList((current) => {
                const newShipmentsList = [...current];
                const shipmentToUpdate = newShipmentsList.find(s => s.id === id);
                if (shipmentToUpdate) {
                    shipmentToUpdate[propertyName] = updatedValue;
                }
                return newShipmentsList;
            });
        };
        return updater;
    }, [setShipmentsList]);
    const [currentShipmentETAInputIndex, setCurrentShipmentETAInputIndex] = useState(0);
    const [exchangeShipmentData, setExchangeShipmentData] = useState(null);
    /* Loading state for pulling exchange shipment data and populating the exchange eta input form */
    const [loadingExchangeShipment, setLoadingExchangeShipment] = useState(false);
    /* Loading state entered into when etas are confirmed for an exchange shipment */
    const [returningFromExchangeShipment, setReturningFromExchangeShipment] = useState(false);
    const currentShipmentData = useMemo(() => ({
        ...shipmentsList[currentShipmentETAInputIndex],
    }), [currentShipmentETAInputIndex, shipmentsList]);
    const { childShipmentId, parentShipmentId, id, userAcceptedExchangeOffer, userEditingExchangeEtas, etasValidated, exchangeEtasValidated, pickupAddress, dropOffAddress, distance, } = currentShipmentData;
    const updateUserAcceptedExchangeOffer = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "userAcceptedExchangeOffer"), [id, createSingleShipmentPropertyValueUpdater]);
    const updateUserEditingExchangeEtas = useMemo(() => createSingleShipmentPropertyValueUpdater(id, "userEditingExchangeEtas"), [id, createSingleShipmentPropertyValueUpdater]);
    /*
      Pulls data for the exchange shipment for the main shipment if the user accepts the offer
    */
    useEffect(() => {
        const exchangeId = childShipmentId || parentShipmentId;
        if (exchangeShipmentData?.id === exchangeId)
            return;
        if (loadingExchangeShipment)
            return;
        if (!userAcceptedExchangeOffer)
            return;
        if (!exchangeId)
            return;
        setLoadingExchangeShipment(true);
        const apiCaller = configPerm["CoreService.AvailableLoads"]
            ? getAvailableTransporterShipment
            : getSingleAdminShipment;
        apiCaller(exchangeId)
            .then(data => {
            setExchangeShipmentData(data);
        })
            .catch(err => {
            console.error(err);
            message.error("Error fetching exchange shipment data");
            setExchangeShipmentData(null);
            updateUserAcceptedExchangeOffer(false);
        })
            .finally(() => {
            setLoadingExchangeShipment(false);
        });
    }, [
        id,
        loadingExchangeShipment,
        updateUserAcceptedExchangeOffer,
        userAcceptedExchangeOffer,
        childShipmentId,
        parentShipmentId,
        configPerm,
        exchangeShipmentData,
    ]);
    /*
      Sets a timed loading state to transition back to main shipments list eta select.
      Happens after etas are confirmed for an exchange shipment
    */
    useEffect(() => {
        if (returningFromExchangeShipment) {
            setTimeout(() => {
                setReturningFromExchangeShipment(false);
                updateUserEditingExchangeEtas(false);
            }, 1500);
        }
    }, [returningFromExchangeShipment, updateUserEditingExchangeEtas]);
    const decrementCurrentShipmentETAInputIndex = useCallback(() => {
        setCurrentShipmentETAInputIndex(curr => (curr - 1 >= 0 ? curr - 1 : curr));
    }, []);
    const incrementCurrentShipmentETAInputIndex = useCallback(() => {
        setCurrentShipmentETAInputIndex(curr => (curr + 1 < shipmentsList.length ? curr + 1 : curr));
    }, [shipmentsList.length]);
    const handleClickYesExchange = useCallback(() => {
        updateUserAcceptedExchangeOffer(true);
        updateUserEditingExchangeEtas(true);
    }, [updateUserAcceptedExchangeOffer, updateUserEditingExchangeEtas]);
    const handleClickNoExchange = useCallback(() => {
        updateUserAcceptedExchangeOffer(false);
        incrementCurrentShipmentETAInputIndex();
    }, [updateUserAcceptedExchangeOffer, incrementCurrentShipmentETAInputIndex]);
    const handleClickCancelExchangeDecision = useCallback(() => {
        updateUserAcceptedExchangeOffer(undefined);
    }, [updateUserAcceptedExchangeOffer]);
    const handleClickEditExchangeEtas = useCallback(() => {
        updateUserEditingExchangeEtas(true);
    }, [updateUserEditingExchangeEtas]);
    if ((userAcceptedExchangeOffer && exchangeEtasValidated === undefined) ||
        userEditingExchangeEtas ||
        returningFromExchangeShipment) {
        if (loadingExchangeShipment || returningFromExchangeShipment) {
            return (React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 400,
                } },
                loadingExchangeShipment && React.createElement(Spin, { tip: "Loading Exchange Data..." }),
                returningFromExchangeShipment && React.createElement(Spin, { tip: "Confirming Exchange ETAs..." })));
        }
        return (React.createElement(EnterExchangeETAs, { mainShipmentData: currentShipmentData, exchangeShipmentData: exchangeShipmentData, createSingleShipmentPropertyValueUpdater: createSingleShipmentPropertyValueUpdater, setReturningFromExchangeShipment: setReturningFromExchangeShipment }));
    }
    return (React.createElement(React.Fragment, null,
        shipmentsList.length > 1 && (React.createElement(ETAInputNavBar, { shipmentsList: shipmentsList, currentShipmentData: currentShipmentData, currentShipmentETAInputIndex: currentShipmentETAInputIndex, decrementCurrentShipmentETAInputIndex: decrementCurrentShipmentETAInputIndex, incrementCurrentShipmentETAInputIndex: incrementCurrentShipmentETAInputIndex, setCurrentShipmentETAInputIndex: setCurrentShipmentETAInputIndex })),
        React.createElement(Card, { className: "noLeftMarginCard", title: React.createElement("div", { style: { fontWeight: "bold", fontSize: 20 } }, `${pickupAddress.simplifyAddress} to
            ${dropOffAddress.simplifyAddress} - ${formatDistance(distance)} miles`), type: "inner", extra: React.createElement(ShipmentTagsList, { shipmentData: currentShipmentData }), style: { marginBottom: 32 } },
            React.createElement(Descriptions, { layout: "vertical", column: 2 },
                React.createElement(Descriptions.Item, { label: "Pickup Instructions", style: { marginRight: 10 } }, pickupAddress?.notes ? pickupAddress.notes : "N/A"),
                React.createElement(Descriptions.Item, { label: "DropOff Instructions" }, dropOffAddress?.notes ? dropOffAddress.notes : "N/A"))),
        React.createElement(EnterETAsInputForm, { etasInputFormRef: etasInputFormRef, shipmentData: currentShipmentData, incrementCurrentShipmentETAInputIndex: incrementCurrentShipmentETAInputIndex, createSingleShipmentPropertyValueUpdater: createSingleShipmentPropertyValueUpdater, hideConfirmButton: hideConfirmButton }),
        currentShipmentData.userAcceptedExchangeOffer === false && (React.createElement(Alert, { type: "warning", showIcon: true, banner: true, message: "Exchange offer was declined.", action: React.createElement(Button, { type: "ghost", onClick: handleClickCancelExchangeDecision }, "Cancel"), style: { marginTop: 16 } })),
        userAcceptedExchangeOffer && exchangeEtasValidated && (React.createElement(Alert, { type: "success", showIcon: true, banner: true, message: "ETAs confirmed for exchange shipment.", action: React.createElement(React.Fragment, null,
                React.createElement(Button, { icon: React.createElement(EditOutlined, null), type: "ghost", onClick: handleClickEditExchangeEtas }, "Edit"),
                React.createElement(Button, { type: "ghost", onClick: handleClickCancelExchangeDecision }, "Cancel")), style: { marginTop: 16 } })),
        etasValidated === false && (React.createElement(Alert, { type: "error", showIcon: true, banner: true, message: "Please reconfirm shipment etas.", style: { marginTop: 16 } })),
        exchangeEtasValidated === false && userAcceptedExchangeOffer && (React.createElement(Alert, { type: "error", showIcon: true, banner: true, message: "Please reconfirm exchange shipment etas.", action: React.createElement(React.Fragment, null,
                React.createElement(Button, { icon: React.createElement(EditOutlined, null), type: "ghost", onClick: handleClickEditExchangeEtas }, "Edit"),
                React.createElement(Button, { type: "ghost", onClick: handleClickCancelExchangeDecision }, "Cancel")), style: { marginTop: 16 } })),
        etasValidatedForAllShipments && (React.createElement(Alert, { type: "success", showIcon: true, message: "ETA input complete", description: 'ETAs have been confirmed for all selected shipments. Please press the "Next" button to continue', style: { marginTop: 16 } })),
        React.createElement(ExchangeOfferAlert, { shipmentData: currentShipmentData, handleClickYesExchange: handleClickYesExchange, handleClickNoExchange: handleClickNoExchange })));
};
EnterETAsInputBody.defaultProps = {
    hideConfirmButton: false,
};
export default EnterETAsInputBody;
